import { createReducer, on } from "@ngrx/store";
import { ChatState } from "./chat.models";
import * as ChatActions from "./chat.actions";
import * as UserActions from "../user/user.actions";
import * as ChatSocketActions from "../websocket/websocket.actions";

export const initialState : ChatState = {
    data : null,
    allData: null,
    activeChannelId: null,
    pagination: null,
    loadingOlder: false,
    uploadingAttachment: false,
    uploadingAttachmentCount: 0,
    error: null,
    message: null,
    success: false,
    validationError: []
};

function mergeChats(existingChats: typeof initialState.allData, incomingChats: typeof initialState.allData, mode: 'replace' | 'prepend') {
    if (mode === 'replace') {
        return incomingChats;
    }

    const merged = [...(incomingChats ?? []), ...(existingChats ?? [])];
    const seen = new Set<string>();

    return merged.filter((chat) => {
        if (!chat?.id) {
            return true;
        }

        if (seen.has(chat.id)) {
            return false;
        }

        seen.add(chat.id);
        return true;
    });
}

export const chatReducer = createReducer(
    initialState,

    on(ChatActions.ChatGetRequest, (state, { payload }) => ({
        ...state,
        activeChannelId: payload.channelId,
        allData: (payload.mode ?? 'replace') === 'replace' ? null : state.allData,
        loadingOlder: (payload.mode ?? 'replace') === 'prepend',
        error: null,
        validationError: []
    })),

    on(ChatActions.ChatGetSuccess, (state, { channelId, mode, response }) => {
        if (state.activeChannelId && state.activeChannelId !== channelId) {
            return state;
        }

        return {
            ...state,
            activeChannelId: channelId,
            allData: mergeChats(state.allData, response.messages, mode),
            pagination: response.pagination,
            loadingOlder: false,
            error: null,
            message: "Chat messages fetched successfully",
            success: true,
            validationError: []
        };
    }),

    on(ChatActions.ChatGetFailure, (state, { error }) => ({
        ...state,
        loadingOlder: false,
        error,
        message: null,
        success: false,
        validationError: []
    })),

    on(ChatActions.ChatValidationError, (state, { validationErrors }) => ({
        ...state,
        error: null,
        message: null,
        success: false,
        validationErrors
    })),

    on(ChatActions.ChatAttachmentUploadRequest, (state) => ({
        ...state,
        uploadingAttachmentCount: state.uploadingAttachmentCount + 1,
        uploadingAttachment: true,
        error: null,
    })),

    on(ChatActions.ChatDeleteSuccess, (state, { message }) => ({
        ...state,
        error: null,
        message,
        success: true,
        validationError: []
    })),

    on(ChatActions.ChatDeleteFailure, (state, { error }) => ({
        ...state,
        error,
        message: null,
        success: false,
        validationError: []
    })),

    on(ChatActions.ChatAttachmentUploadSuccess, (state, { message }) => ({
        ...state,
        uploadingAttachmentCount: Math.max(0, state.uploadingAttachmentCount - 1),
        uploadingAttachment: Math.max(0, state.uploadingAttachmentCount - 1) > 0,
        error: null,
        message,
        success: true,
        validationError: []
    })),

    on(ChatActions.ChatAttachmentUploadFailure, (state, { error }) => ({
        ...state,
        uploadingAttachmentCount: Math.max(0, state.uploadingAttachmentCount - 1),
        uploadingAttachment: Math.max(0, state.uploadingAttachmentCount - 1) > 0,
        error,
        message: null,
        success: false,
        validationError: []
    })),

    on(ChatActions.ChatEditSuccess, (state, { message }) => ({
        ...state,
        error: null,
        message,
        success: true,
        validationError: []
    })),

    on(ChatActions.ChatEditFailure, (state, { error }) => ({
        ...state,
        error,
        message: null,
        success: false,
        validationError: []
    })),

    on(ChatSocketActions.receiveChannelMessage, (state, { message }) => {
        if (!state.activeChannelId || state.activeChannelId !== message.channel_id) {
            return state;
        }

        const existingChats = state.allData ?? [];
        const existingIndex = existingChats.findIndex((chat) => chat.id === message._id);

        const nextChat = {
            id: message._id,
            channel_id: message.channel_id,
            organization_id: message.organization_id,
            sender_id: message.sender_id,
            name: message.name,
            message_type: message.message_type,
            message: message.message,
            client_message_id: message.client_message_id ?? '',
            attributes: message.attributes,
            created_at: message.created_at,
        };

        if (existingIndex >= 0) {
            const nextChats = [...existingChats];
            nextChats[existingIndex] = nextChat;
            return {
                ...state,
                allData: nextChats,
            };
        }

        return {
            ...state,
            allData: [...existingChats, nextChat],
        };
    }),

    on(UserActions.UserProfilePatched, (state, { user }) => ({
        ...state,
        data: state.data?.sender_id === user.id ? { ...state.data, name: user.name } : state.data,
        allData: state.allData?.map((chat) => chat.sender_id === user.id ? { ...chat, name: user.name } : chat) ?? state.allData,
    })),


);
